import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type Query } from "@tanstack/react-query";
import type { OrderView, RoomTarget } from "@townsy/contracts";
import {
  fetchBootstrap,
  fetchCatalog,
  fetchInventory,
  fetchMe,
  fetchMyHome,
  fetchMyOrders,
  fetchMyPermissions,
  fetchOrder,
  fetchPaymentsConfig,
  fetchPresence,
  fetchProperties,
  fetchProperty,
  fetchPropertyHistory,
  fetchPropertyStats,
  fetchRealEstateListings,
  fetchStatsOverview,
  fetchWalletHistory,
  fetchWallets,
  postOutboundIntent,
  postRoomTicket,
  type WalletUnit,
} from "./endpoints";
import { isApiError } from "./client";

export const queryKeys = {
  bootstrap: ["bootstrap"] as const,
  properties: ["properties"] as const,
  property: (id: string) => ["property", id] as const,
  propertyHistory: (id: string) => ["property-history", id] as const,
  propertyStats: (id: string) => ["property-stats", id] as const,
  statsOverview: ["stats-overview"] as const,
  presence: ["presence"] as const,
  me: ["me"] as const,
  wallets: ["wallets"] as const,
  walletHistory: (unit: WalletUnit) => ["wallets", "history", unit] as const,
  realEstateListings: ["real-estate-listings"] as const,
  myPermissions: (id: string) => ["my-permissions", id] as const,
  catalog: ["catalog"] as const,
  inventory: ["inventory"] as const,
  myHome: ["my-home"] as const,
  paymentsConfig: ["payments-config"] as const,
  order: (id: string) => ["order", id] as const,
  myOrders: ["my-orders"] as const,
  notifications: ["notifications"] as const,
};

/** Dollar purchases on/off, provider and test-mode notice (public; refreshed every few minutes). */
export function usePaymentsConfigQuery(enabled = true) {
  return useQuery({ queryKey: queryKeys.paymentsConfig, queryFn: fetchPaymentsConfig, enabled, staleTime: 120_000 });
}

/**
 * One of the buyer's own orders. `pollMs` > 0 keeps re-reading it (status pages poll until the
 * order settles); the server's answer is the only source of truth for success or refunds.
 */
export type OrderPoll = number | false | ((query: Query<OrderView, Error, OrderView, ReturnType<typeof queryKeys.order>>) => number | false | undefined);
export function useOrderQuery(id: string | null, enabled: boolean, pollMs: OrderPoll = false) {
  return useQuery({
    queryKey: queryKeys.order(id ?? "none"),
    queryFn: ({ signal }) => fetchOrder(id as string, signal),
    enabled: Boolean(id) && enabled,
    staleTime: 0,
    refetchInterval: pollMs,
    retry: no401Retry,
  });
}

export function useMyOrdersQuery(enabled: boolean) {
  return useInfiniteQuery({
    queryKey: queryKeys.myOrders,
    queryFn: ({ pageParam }) => fetchMyOrders(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    retry: no401Retry,
  });
}

/** Listings stay fresh while the Real Estate office is open (other buyers do not notify this room). */
export const LISTINGS_REFRESH_MS = 15_000;

const no401Retry = (count: number, err: unknown) => !(isApiError(err) && (err.status === 401 || err.status === 403)) && count < 2;

/** Coins + Credit for a signed-in resident (the API answers 401 for guests, so guests never ask). */
export function useWalletsQuery(signedIn: boolean) {
  return useQuery({
    queryKey: queryKeys.wallets,
    queryFn: fetchWallets,
    enabled: signedIn,
    staleTime: 30_000,
    retry: no401Retry,
  });
}

export function useWalletHistoryQuery(unit: WalletUnit, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: queryKeys.walletHistory(unit),
    queryFn: ({ pageParam }) => fetchWalletHistory(unit, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    retry: no401Retry,
  });
}

export function useRealEstateListingsQuery(enabled: boolean, live = false) {
  return useQuery({
    queryKey: queryKeys.realEstateListings,
    queryFn: fetchRealEstateListings,
    enabled,
    staleTime: 10_000,
    refetchInterval: live ? LISTINGS_REFRESH_MS : false,
  });
}

/** What the signed-in visitor may do with one property (all false for guests, so guests skip the call). */
export function useMyPermissionsQuery(id: string | null, signedIn: boolean) {
  return useQuery({
    queryKey: queryKeys.myPermissions(id ?? "none"),
    queryFn: () => fetchMyPermissions(id as string),
    enabled: Boolean(id) && signedIn,
    staleTime: 30_000,
    retry: no401Retry,
  });
}

/** Public home levels + items with their offers (prices change rarely; refreshed after a PRICE_CHANGED). */
export function useCatalogQuery(enabled = true) {
  return useQuery({ queryKey: queryKeys.catalog, queryFn: fetchCatalog, enabled, staleTime: 60_000 });
}

/** The signed-in resident's items (stored and placed); guests have none and never ask. */
export function useInventoryQuery(signedIn: boolean) {
  return useQuery({ queryKey: queryKeys.inventory, queryFn: fetchInventory, enabled: signedIn, staleTime: 30_000, retry: no401Retry });
}

/** The signed-in resident's home (null when they own no plot). */
export function useMyHomeQuery(signedIn: boolean) {
  return useQuery({ queryKey: queryKeys.myHome, queryFn: fetchMyHome, enabled: signedIn, staleTime: 30_000, retry: no401Retry });
}

export const STATS_REFRESH_MS = 30_000;
export const PRESENCE_REFRESH_MS = 30_000;

export function useBootstrapQuery(enabled = true) {
  return useQuery({
    queryKey: queryKeys.bootstrap,
    queryFn: ({ signal }) => fetchBootstrap(signal),
    staleTime: 60_000,
    enabled,
  });
}

export function usePropertiesQuery(enabled = true) {
  return useQuery({ queryKey: queryKeys.properties, queryFn: () => fetchProperties(), enabled, staleTime: 60_000 });
}

export function usePropertyQuery(id: string | null) {
  return useQuery({
    queryKey: queryKeys.property(id ?? "none"),
    queryFn: () => fetchProperty(id as string),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function usePropertyHistoryQuery(id: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.propertyHistory(id ?? "none"),
    queryFn: () => fetchPropertyHistory(id as string),
    enabled: Boolean(id) && enabled,
  });
}

export function usePropertyStatsQuery(id: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.propertyStats(id ?? "none"),
    queryFn: () => fetchPropertyStats(id as string),
    enabled: Boolean(id) && enabled,
    refetchInterval: enabled ? STATS_REFRESH_MS : false,
  });
}

/** Public statistics; refetched every 30 s while the panel/page is open. */
export function useStatsOverviewQuery(open: boolean) {
  return useQuery({
    queryKey: queryKeys.statsOverview,
    queryFn: fetchStatsOverview,
    enabled: open,
    refetchInterval: open ? STATS_REFRESH_MS : false,
    staleTime: 10_000,
  });
}

export function usePresenceQuery(enabled = true) {
  return useQuery({
    queryKey: queryKeys.presence,
    queryFn: fetchPresence,
    enabled,
    refetchInterval: enabled ? PRESENCE_REFRESH_MS : false,
    staleTime: 10_000,
  });
}

export function useMeQuery(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: fetchMe,
    enabled,
    retry: (count, err) => !(isApiError(err) && err.status === 401) && count < 2,
  });
}

export function useRoomTicketMutation() {
  return useMutation({ mutationFn: (target: RoomTarget) => postRoomTicket(target) });
}

export function useOutboundIntentMutation() {
  return useMutation({ mutationFn: (propertyId: string) => postOutboundIntent(propertyId) });
}

/** Invalidate everything that depends on who the visitor is (after sign in / out). */
export function useInvalidateIdentity() {
  const qc = useQueryClient();
  return async () => {
    // Balances and permissions belong to the previous identity: drop them instead of showing stale numbers.
    qc.removeQueries({ queryKey: queryKeys.wallets });
    qc.removeQueries({ queryKey: ["my-permissions"] });
    qc.removeQueries({ queryKey: queryKeys.inventory });
    qc.removeQueries({ queryKey: queryKeys.myHome });
    qc.removeQueries({ queryKey: queryKeys.myOrders });
    qc.removeQueries({ queryKey: ["order"] });
    qc.removeQueries({ queryKey: queryKeys.notifications });
    // Staff permissions, the audit log and the role list (ADR-0035) belong to the previous account too.
    qc.removeQueries({ queryKey: ["admin"] });
    // Two-step sign-in state and this session's staff confirmation (ADR-0039).
    qc.removeQueries({ queryKey: ["two-factor"] });
    await Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.bootstrap }),
      qc.invalidateQueries({ queryKey: queryKeys.me }),
      qc.invalidateQueries({ queryKey: queryKeys.properties }),
      // Single-property reads carry owner-only actions (edit_board, edit_interior) for the signed-in account.
      qc.invalidateQueries({ queryKey: ["property"] }),
      qc.invalidateQueries({ queryKey: queryKeys.realEstateListings }),
    ]);
  };
}
